// NSARA Stores V2 — onglet "Commandes" du workspace boutique (section 13).
//
// Source unique: /api/orders filtré par shopId (backend/routes/orders.js) —
// une boutique ne voit JAMAIS les commandes d'une autre boutique.
//
// Aucune logique métier n'est réécrite ici: la confirmation passe par
// PATCH /api/orders/:id/status et la transformation en vente par la RPC
// convert_order_to_sale (qui réutilise create_sale: CA, stock et encaissement
// restent exclusivement pilotés côté serveur, comme dans Product Core).
//
// Les lignes de produit affichées sur la carte proviennent de
// GET /api/orders/:id, chargées pour les 10 commandes les plus récentes
// uniquement (borné, en lecture seule, et remis à zéro à chaque changement de
// boutique ou de filtre — jamais de résidu d'une boutique précédente).
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Inbox, Check, ShoppingCart, X } from 'lucide-react';
import { getOrders, getOrder, updateOrderStatus, convertOrderToSale } from '../../services/apiClient';
import Modal from './Modal';
import { formatMoney, formatMoneyShort, formatWhen, ORDER_TABS, ORDER_STATUS_LABELS } from './storeUtils';

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Espèces' },
  { value: 'ORANGE_MONEY', label: 'Orange Money' },
  { value: 'WAVE', label: 'Wave' },
  { value: 'BANK_TRANSFER', label: 'Virement' },
  { value: 'OTHER', label: 'Autre' }
];

const ENRICH_LIMIT = 10;

const statusClass = (status) => {
  if (status === 'PENDING') return 'stores-badge stores-badge--low stores-badge--plain';
  if (status === 'COMPLETED') return 'stores-badge stores-badge--active';
  if (status === 'CANCELLED') return 'stores-badge stores-badge--paused';
  return 'stores-badge stores-badge--live';
};

const summarizeItems = (items) => (items || [])
  .map((item) => `${item.product_name_snapshot} × ${Number(item.quantity)}`)
  .join(', ');

export default function StoreOrdersTab({ businessId, shopId, initialStatus = '', onConverted }) {
  const [orders, setOrders] = useState([]);
  const [itemsById, setItemsById] = useState({});
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState('');
  const [openId, setOpenId] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');

  const load = useCallback(async () => {
    if (!businessId || !shopId) return;
    setLoading(true);
    setError('');
    try {
      const { orders: rows } = await getOrders({ businessId, shopId, status: status || undefined, limit: 50 });
      const list = rows || [];
      setOrders(list);
      setItemsById({});
      // Enrichissement borné: échec silencieux, la carte reste lisible sans ligne produit.
      await Promise.all(list.slice(0, ENRICH_LIMIT).map(async (order) => {
        try {
          const res = await getOrder(order.id);
          setItemsById((current) => ({ ...current, [order.id]: summarizeItems(res.items) }));
        } catch (err) { /* lecture optionnelle */ }
      }));
    } catch (err) {
      setError(err?.message || 'Impossible de charger les commandes.');
    } finally {
      setLoading(false);
    }
  }, [businessId, shopId, status]);

  // shopId + status dans les dépendances, état vidé avant l'appel: aucun
  // résidu de la boutique précédente (section 8 / test F).
  useEffect(() => { setOrders([]); setItemsById({}); setDetail(null); setOpenId(''); load(); }, [load]);

  useEffect(() => { setStatus(initialStatus); }, [initialStatus]);

  const flash = (message) => { setNotice(message); window.setTimeout(() => setNotice(''), 3200); };

  const openDetail = async (order) => {
    setOpenId(order.id);
    setDetail(null);
    setDetailLoading(true);
    setAmountPaid('');
    setPaymentMethod('CASH');
    setError('');
    try {
      const res = await getOrder(order.id);
      setDetail(res);
    } catch (err) {
      setError(err?.message || 'Impossible de charger cette commande.');
      setOpenId('');
    } finally {
      setDetailLoading(false);
    }
  };

  const act = async (order, action, successMessage) => {
    setBusyId(order.id);
    setError('');
    try {
      await action(order);
      flash(successMessage);
      setOpenId('');
      setDetail(null);
      await load();
      onConverted?.();
    } catch (err) {
      setError(err?.message || 'Action impossible.');
    } finally {
      setBusyId('');
    }
  };

  const confirm = (order) => act(order, async (row) => {
    await updateOrderStatus(row.id, 'CONFIRMED');
  }, 'Commande confirmée.');

  const cancel = (order) => act(order, async (row) => {
    await updateOrderStatus(row.id, 'CANCELLED');
  }, 'Commande annulée.');

  const convert = (order) => act(order, async (row) => {
    const paid = amountPaid === '' ? undefined : Number(amountPaid);
    await convertOrderToSale(row.id, { amountPaid: paid, paymentMethod });
  }, 'Vente enregistrée : chiffre d’affaires et stock mis à jour.');

  const busy = busyId !== '';

  return (
    <div className="stores-section">
      <div className="stores-section-head">
        <div className="stores-head-text">
          <h2 className="stores-section-title">Commandes</h2>
          <p className="stores-section-sub">Les commandes reçues par cette boutique, et rien d’autre.</p>
        </div>
      </div>

      {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}
      {notice && <p className="stores-alert stores-alert--success" role="status">{notice}</p>}

      <div className="stores-chips" role="group" aria-label="Filtrer les commandes">
        {ORDER_TABS.map((tab) => (
          <button
            key={tab.id || 'all'}
            type="button"
            className={status === tab.id ? 'stores-chip is-active' : 'stores-chip'}
            aria-pressed={status === tab.id}
            onClick={() => setStatus(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="stores-order-list" aria-busy="true" aria-label="Chargement des commandes">
          {[0, 1].map((key) => <div key={key} className="stores-skeleton-row" style={{ height: 96 }} />)}
        </div>
      )}

      {!loading && orders.length === 0 && (
        <div className="stores-empty">
          <span className="stores-empty-icon" aria-hidden="true"><Inbox size={22} /></span>
          <h3>{status ? 'Aucune commande dans ce filtre' : 'Aucune commande pour le moment'}</h3>
          <p>Partagez le lien d’un produit ou la vitrine de votre boutique pour recevoir vos premières commandes.</p>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="stores-order-list">
          {orders.map((order) => {
            const isBusy = busyId === order.id;
            const lines = itemsById[order.id];
            return (
              <article className="stores-order-card" key={order.id}>
                <div className="stores-order-top">
                  <div>
                    <p className="stores-order-number">{order.order_number}</p>
                    <p className="stores-order-client">{order.customer_name}</p>
                    {lines && <p className="stores-order-lines">{lines}</p>}
                  </div>
                  <span className={statusClass(order.status)}>{ORDER_STATUS_LABELS[order.status] || order.status}</span>
                </div>
                <div className="stores-order-foot">
                  <span className="stores-order-total">{formatMoneyShort(order.subtotal_estimated)}</span>
                  <span className="stores-order-when">{formatWhen(order.created_at)}</span>
                </div>
                <div className="stores-form-actions" style={{ marginTop: 10 }}>
                  <button type="button" className="stores-btn stores-btn--outline stores-btn--sm" disabled={busy} onClick={() => openDetail(order)}>
                    {isBusy ? <Loader2 size={15} aria-hidden="true" className="stores-spin" /> : <ShoppingCart size={15} aria-hidden="true" />}
                    Voir la commande
                  </button>
                  {order.status === 'PENDING' && (
                    <button type="button" className="stores-btn stores-btn--primary stores-btn--sm" disabled={busy} onClick={() => confirm(order)}>
                      <Check size={15} aria-hidden="true" /> Confirmer
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal open={Boolean(openId)} onClose={() => { if (!busy) { setOpenId(''); setDetail(null); } }} labelledBy="order-detail-title" size="lg">
        <div className="stores-dialog">
          <div className="stores-dialog-head">
            <span className="stores-dialog-icon" aria-hidden="true"><ShoppingCart size={18} /></span>
            <div>
              <h2 id="order-detail-title" className="stores-dialog-title">{detail?.order?.order_number || 'Commande'}</h2>
              <p className="stores-dialog-subtitle">
                {detail ? `${ORDER_STATUS_LABELS[detail.order.status] || detail.order.status} · ${formatWhen(detail.order.created_at)}` : 'Chargement…'}
              </p>
            </div>
          </div>

          {detailLoading && <p className="stores-hint" aria-busy="true">Chargement de la commande…</p>}

          {detail && (
            <>
              <div className="stores-detail-block" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                <p className="stores-detail-label">Articles</p>
                {(detail.items || []).map((item) => (
                  <div className="stores-detail-item" key={item.id}>
                    <span>{item.product_name_snapshot} × {Number(item.quantity)}</span>
                    <strong>{formatMoney(item.subtotal)}</strong>
                  </div>
                ))}
                <div className="stores-detail-total">
                  <span>Total</span>
                  <span>{formatMoney(detail.order.subtotal_estimated)}</span>
                </div>
              </div>

              <div className="stores-detail-grid">
                <div>
                  <p className="stores-detail-label">Client</p>
                  <p className="stores-detail-value">{detail.order.customer_name}</p>
                  <p className="stores-detail-label" style={{ marginTop: 10 }}>Téléphone</p>
                  <p className="stores-detail-value">{detail.order.customer_phone}</p>
                </div>
                <div>
                  <p className="stores-detail-label">Adresse / quartier</p>
                  <p className="stores-detail-value">{detail.order.delivery_address || 'Non renseignée'}</p>
                  {detail.order.customer_email && (
                    <>
                      <p className="stores-detail-label" style={{ marginTop: 10 }}>Email</p>
                      <p className="stores-detail-value">{detail.order.customer_email}</p>
                    </>
                  )}
                </div>
              </div>

              {detail.order.notes && (
                <div className="stores-detail-block">
                  <p className="stores-detail-label">Note du client</p>
                  <p className="stores-detail-value">{detail.order.notes}</p>
                </div>
              )}

              {detail.order.status === 'COMPLETED' && (
                <p className="stores-alert stores-alert--success">
                  Cette commande est déjà transformée en vente : chiffre d’affaires, stock et encaissement sont enregistrés.
                </p>
              )}
              {detail.order.status === 'CANCELLED' && (
                <p className="stores-alert stores-alert--info">Commande annulée : aucune vente, aucun mouvement de stock.</p>
              )}

              {(detail.order.status === 'PENDING' || detail.order.status === 'CONFIRMED') && (
                <div className="stores-detail-block">
                  <p className="stores-detail-label">Transformer en vente</p>
                  <p className="stores-hint" style={{ marginBottom: 10 }}>
                    C’est cette action qui enregistre le chiffre d’affaires et décrémente le stock.
                    Confirmer seule ne crée aucune vente.
                  </p>
                  <div className="stores-field-row">
                    <div className="stores-field">
                      <label className="stores-label" htmlFor="order-amount">Montant reçu (facultatif)</label>
                      <input
                        id="order-amount"
                        className="stores-input"
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        value={amountPaid}
                        onChange={(event) => setAmountPaid(event.target.value)}
                        placeholder={`${formatMoney(detail.order.subtotal_estimated)} (payé en totalité)`}
                        disabled={busy}
                      />
                    </div>
                    <div className="stores-field">
                      <label className="stores-label" htmlFor="order-payment">Mode de paiement</label>
                      <select id="order-payment" className="stores-input" value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} disabled={busy}>
                        {PAYMENT_METHODS.map((method) => <option key={method.value} value={method.value}>{method.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="stores-dialog-actions">
                {detail.order.status === 'PENDING' && (
                  <button type="button" className="stores-btn stores-btn--outline" disabled={busy} onClick={() => confirm(detail.order)}>
                    <Check size={15} aria-hidden="true" /> Confirmer
                  </button>
                )}
                {(detail.order.status === 'PENDING' || detail.order.status === 'CONFIRMED') && (
                  <button type="button" className="stores-btn stores-btn--primary" disabled={busy} onClick={() => convert(detail.order)}>
                    {busy && <Loader2 size={15} aria-hidden="true" className="stores-spin" />}
                    Transformer en vente
                  </button>
                )}
                {(detail.order.status === 'PENDING' || detail.order.status === 'CONFIRMED') && (
                  <button type="button" className="stores-btn stores-btn--danger" disabled={busy} onClick={() => cancel(detail.order)}>
                    <X size={15} aria-hidden="true" /> Annuler
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
